// NEW_FILE_JUSTIFIED: Employee-specific document section with named slots, adapted from PolicyDocumentsSection
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in company/ need convex api
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { FileText, Download, Upload, File, FolderOpen, ZoomIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEmployeeDocumentUpload } from '@/hooks/useEmployeeDocumentUpload';
import type { IEmployeeDocumentsSectionProps, IEmployeeDocument } from '@/@types/company';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated

// ─── File type helpers ────────────────────────────────────────────────────────

function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toLowerCase() : '';
}

function isImageFile(fileName: string): boolean {
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(getFileExtension(fileName));
}

function isPdfFile(fileName: string): boolean {
  return getFileExtension(fileName) === 'pdf';
}

// ─── PDF Thumbnail ─────────────────────────────────────────────────────────────

function PdfThumbnail({ url, scale = 0.8 }: { url: string; scale?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) setLoaded(true);
      } catch {
        // PDF rendering failed — fallback icon shown
      }
    }

    void render();
    return () => {
      cancelled = true;
    };
  }, [url, scale]);

  return (
    <>
      {!loaded && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
      <canvas
        ref={canvasRef}
        className="w-full h-auto"
        style={{ display: loaded ? 'block' : 'none' }}
        role="img"
        aria-label="PDF preview"
      />
    </>
  );
}

// ─── Single document slot ─────────────────────────────────────────────────────

function DocumentSlot({
  label,
  doc,
  editable,
  onUpload,
  onRemove,
  isUploading,
}: {
  label: string;
  doc: IEmployeeDocument | undefined;
  editable: boolean;
  onUpload: () => void;
  onRemove: () => void;
  isUploading: boolean;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const storageId = doc?.storageId as Id<'_storage'> | undefined; // AS_JUSTIFIED: IEmployeeDocument.storageId is Id<'_storage'> from Convex
  const fileUrl = useQuery(api.storage.getUrl, storageId ? { storageId } : 'skip');

  const isImage = doc ? isImageFile(doc.fileName) : false;
  const isPdf = doc ? isPdfFile(doc.fileName) : false;
  const canPreview = !!fileUrl && (isImage || isPdf);
  const isLoadingUrl = doc !== undefined && fileUrl === undefined;

  return (
    <>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-[90vw] p-0 overflow-hidden">
          <DialogTitle className="sr-only">{doc?.fileName ?? label}</DialogTitle>
          {isImage && fileUrl && (
            <img
              src={fileUrl}
              alt={doc?.fileName}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
          {isPdf && fileUrl && (
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              title={doc?.fileName}
              className="w-full h-[85vh] border-0"
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>

        <div className="rounded-lg border border-border overflow-hidden bg-card">
          {/* Thumbnail area */}
          <div
            className={cn(
              'h-56 relative flex items-center justify-center bg-muted/20 overflow-hidden',
              canPreview && 'cursor-pointer group'
            )}
            role={canPreview ? 'button' : 'img'}
            aria-label={
              canPreview
                ? `Preview ${doc?.fileName}`
                : doc
                  ? `${label}: ${doc.fileName}`
                  : `${label}: no document uploaded`
            }
            onClick={canPreview ? () => setPreviewOpen(true) : undefined}
          >
            {/* Upload-in-progress skeleton */}
            {isUploading && <Skeleton className="absolute inset-0 rounded-none" />}

            {/* Empty state */}
            {!isUploading && !doc && (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <FolderOpen className="w-10 h-10" aria-hidden="true" />
                <span className="text-xs">No document</span>
              </div>
            )}

            {/* Fetching URL */}
            {!isUploading && isLoadingUrl && <Skeleton className="absolute inset-0 rounded-none" />}

            {/* URL not found */}
            {!isUploading && doc !== undefined && fileUrl === null && (
              <File className="w-10 h-10 text-muted-foreground" aria-hidden="true" />
            )}

            {/* Image */}
            {!isUploading && doc !== undefined && fileUrl && isImage && (
              <img src={fileUrl} alt={doc.fileName} className="w-full h-full object-cover" />
            )}

            {/* PDF */}
            {!isUploading && doc !== undefined && fileUrl && isPdf && (
              <PdfThumbnail url={fileUrl} />
            )}

            {/* Unknown file type */}
            {!isUploading && doc !== undefined && fileUrl && !isImage && !isPdf && (
              <File className="w-10 h-10 text-muted-foreground" aria-hidden="true" />
            )}

            {/* Hover zoom overlay */}
            {canPreview && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
                <ZoomIn
                  className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 flex items-center gap-2 min-w-0 border-t border-border">
            {isUploading && (
              <span className="text-xs text-muted-foreground flex-1">Uploading…</span>
            )}
            {!isUploading && doc && (
              <span className="text-xs text-foreground truncate flex-1" title={doc.fileName}>
                {doc.fileName}
              </span>
            )}
            {!isUploading && !doc && (
              <span className="text-xs text-muted-foreground flex-1">—</span>
            )}

            {doc && fileUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                asChild
              >
                <a href={fileUrl} download={doc.fileName}>
                  <Download className="w-3.5 h-3.5" aria-hidden="true" />
                  <span className="sr-only">Download {doc.fileName}</span>
                </a>
              </Button>
            )}

            {editable && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                onClick={onUpload}
                disabled={isUploading}
                aria-label={doc ? `Replace ${label}` : `Upload ${label}`}
              >
                <Upload className="w-3 h-3 mr-1" aria-hidden="true" />
                {doc ? 'Replace' : 'Upload'}
              </Button>
            )}

            {editable && doc && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-destructive hover:text-destructive shrink-0"
                onClick={onRemove}
                disabled={isUploading}
                aria-label={`Remove ${label}`}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main section ─────────────────────────────────────────────────────────────

type TDocumentField = // LOCAL_TYPE_JUSTIFIED: narrow alias used only in this file for handler signatures
  'identityDocFront' | 'identityDocBack' | 'drivingLicenseFront' | 'drivingLicenseBack';

export function EmployeeDocumentsSection({
  identityDocFront,
  identityDocBack,
  drivingLicenseFront,
  drivingLicenseBack,
  editable = false,
  onDocumentChange,
}: IEmployeeDocumentsSectionProps) {
  const { uploadDocument } = useEmployeeDocumentUpload();
  const [uploadingField, setUploadingField] = useState<TDocumentField | null>(null);

  const handleUpload = useCallback(
    async (field: TDocumentField) => {
      setUploadingField(field);
      try {
        const doc = await uploadDocument();
        if (doc) onDocumentChange?.(field, doc);
      } finally {
        setUploadingField(null);
      }
    },
    [uploadDocument, onDocumentChange]
  );

  const handleRemove = useCallback(
    (field: TDocumentField) => {
      onDocumentChange?.(field, null);
    },
    [onDocumentChange]
  );

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
          Identity Documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <DocumentSlot
            label="ID / Passport — Front"
            doc={identityDocFront}
            editable={editable}
            onUpload={() => {
              void handleUpload('identityDocFront');
            }}
            onRemove={() => handleRemove('identityDocFront')}
            isUploading={uploadingField === 'identityDocFront'}
          />
          <DocumentSlot
            label="ID / Passport — Back"
            doc={identityDocBack}
            editable={editable}
            onUpload={() => {
              void handleUpload('identityDocBack');
            }}
            onRemove={() => handleRemove('identityDocBack')}
            isUploading={uploadingField === 'identityDocBack'}
          />
          <DocumentSlot
            label="Driving Licence — Front"
            doc={drivingLicenseFront}
            editable={editable}
            onUpload={() => {
              void handleUpload('drivingLicenseFront');
            }}
            onRemove={() => handleRemove('drivingLicenseFront')}
            isUploading={uploadingField === 'drivingLicenseFront'}
          />
          <DocumentSlot
            label="Driving Licence — Back"
            doc={drivingLicenseBack}
            editable={editable}
            onUpload={() => {
              void handleUpload('drivingLicenseBack');
            }}
            onRemove={() => handleRemove('drivingLicenseBack')}
            isUploading={uploadingField === 'drivingLicenseBack'}
          />
        </div>
      </CardContent>
    </Card>
  );
}
